import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useSession from '@/hooks/useSession';
import { AuthService } from '@/services/auth.service';
import { toast } from 'react-toastify';
import HomeLayout from '@/layouts/HomeLayout';
import SEOHelmet from '@/components/SEOHelmet';

/**
 * SSO callback handler page.
 * Handles the OAuth2 callback from Zitadel, exchanges code for tokens,
 * and redirects to the app on success or sign-in on failure.
 *
 * Uses refs to prevent duplicate API calls in React 18 StrictMode,
 * which double-mounts components in development mode.
 */
const SSOCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { session, setSession } = useSession();
  const callbackProcessed = useRef(false);
  const callbackSucceeded = useRef(false);
  // Track which code we've processed to allow retry with a new OAuth code
  const processedCode = useRef<string | null>(null);

  // Effect to handle the SSO callback
  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');

      // Prevent duplicate calls from React 18 StrictMode double-mount.
      // We check both the processed flag AND the code to allow genuine retries
      // (a new SSO attempt will have a different code from Zitadel).
      if (callbackProcessed.current && processedCode.current === code) {
        return;
      }
      callbackProcessed.current = true;
      processedCode.current = code;
      const state = searchParams.get('state');

      if (!code || !state) {
        toast('Missing SSO parameters', { type: 'error' });
        navigate('/sign-in', { replace: true });
        return;
      }

      // Client-side state validation (defense-in-depth alongside backend validation)
      const storedState = sessionStorage.getItem('sso_oauth_state');
      // Clear stored state immediately (one-time use)
      sessionStorage.removeItem('sso_oauth_state');

      if (!storedState || storedState !== state) {
        // Missing or mismatched state - potential CSRF attack
        toast.error('Security validation failed. Please try signing in again.');
        navigate('/sign-in', { replace: true });
        return;
      }
      try {
        const newSession = await AuthService.handleSSOCallback(code, state);
        callbackSucceeded.current = true;
        setSession(newSession);
        // Don't navigate here - let the session effect handle it
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'An unknown error occurred.';
        // Check if this is a seat limit error (403) - status is added by auth.service.ts
        const isSeatLimitError =
          (err as { status?: number }).status === 403 ||
          message.toLowerCase().includes('seat limit');

        if (isSeatLimitError) {
          // Show persistent error for seat limit - user must dismiss manually
          toast.error(message, {
            autoClose: false,
            closeOnClick: false,
            draggable: false,
            toastId: 'seat-limit-error', // Prevent duplicates
          });
        } else {
          toast.error(message);
        }

        navigate('/sign-in', { replace: true });
      }
    };

    handleCallback();
  }, [navigate, searchParams, setSession]);

  // Effect to navigate after session is set
  useEffect(() => {
    if (callbackSucceeded.current && session && session.email) {
      navigate('/', { replace: true });
    }
  }, [session, navigate]);

  // Show loading UI while processing - component will unmount on navigation
  return (
    <HomeLayout>
      <SEOHelmet
        title="Signing In"
        description="Completing SSO sign in"
        index={false}
      />
      <h1>Completing sign in...</h1>
      <p>Please wait while we complete your authentication.</p>
    </HomeLayout>
  );
};

export default SSOCallback;
